/* Subscribe & Save — carousel + tab filter + subscribe-only init
 *
 * One instance per section root.
 * - Tabs filter slides by collection-handle membership (data-product-collections vs data-sns-tab).
 *   First tab is treated as "All" (shows everything regardless of its handle).
 * - Arrows scroll one page of visible cards at a time.
 * - On init: each card's Subscribe radio is force-checked + the CTA span text swaps to "SUBSCRIBE NOW".
 * - Tab clicks call preventDefault so the page doesn't navigate to the underlying /collections/* link.
 *   (The href is preserved for no-JS / right-click scenarios.)
 */
(function () {
  'use strict';

  function initSubscribeRadios(root) {
    root.querySelectorAll('input[type="radio"][value="subscribe"]').forEach(function (radio) {
      if (radio.checked) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    });
    root.querySelectorAll('.product-card-enhanced__add-to-cart > span').forEach(function (span) {
      span.textContent = 'SUBSCRIBE NOW';
    });
  }

  /* Promote the first ~6 cards' primary images to eager so the initially-visible
     deck (max 3 cards on desktop) plus the next page-worth of cards (one arrow
     click away) are ready without the "catch-up" flash described in the original
     pass. Everything else stays lazy so init doesn't fire ~50 simultaneous image
     fetches — that was hurting LCP on Safari Private Relay and slow connections. */
  function preloadCarouselImages(root) {
    var EAGER_PRIMARY_COUNT = 6;
    root.querySelectorAll('[data-sns-slide]').forEach(function (slide, idx) {
      var primary = slide.querySelector('img');
      if (!primary) return;
      primary.decoding = 'async';
      if (idx < EAGER_PRIMARY_COUNT) {
        if (primary.loading === 'lazy') primary.loading = 'eager';
      } else if (!primary.loading) {
        primary.loading = 'lazy';
      }
    });
  }

  /* On the Subscribe & Save page, hide variant option values that have no
     subscription-eligible variant. Also rewrite the selected variant to the
     first eligible one if the card defaulted to an ineligible variant.
     Reads the per-product variant JSON the card writes to a sibling <script>. */
  function filterIneligibleVariants(root) {
    root.querySelectorAll('product-card-enhanced').forEach(function (card) {
      var jsonScript = card.querySelector('script[data-variant-json]');
      if (!jsonScript) return;
      var variants;
      try { variants = JSON.parse(jsonScript.textContent); } catch (e) { return; }
      if (!Array.isArray(variants) || !variants.length) return;

      var eligible = variants.filter(function (v) { return v.has_selling_plans && v.available; });
      if (!eligible.length) {
        // Defensive — section-liquid filter should have prevented this, but if
        // it slipped through, hide the card entirely instead of breaking layout.
        var slide = card.closest('.sns-carousel__slide');
        if (slide) slide.dataset.snsHidden = 'true';
        return;
      }

      // For each option position (1, 2, 3), determine which option values appear
      // in at least one eligible variant. Hide other <option> elements.
      [1, 2, 3].forEach(function (position) {
        var key = 'option' + position;
        var allowed = {};
        eligible.forEach(function (v) { if (v[key]) allowed[v[key]] = true; });

        card.querySelectorAll('select[data-option-position="' + position + '"]').forEach(function (sel) {
          var firstAllowed = null;
          Array.prototype.slice.call(sel.options).forEach(function (opt) {
            if (allowed[opt.value]) {
              if (!firstAllowed) firstAllowed = opt.value;
            } else {
              // Hide rather than remove so any in-flight scripts that reference
              // the original option list don't choke.
              opt.disabled = true;
              opt.hidden = true;
            }
          });
          // If the currently selected value was just hidden, switch to the
          // first allowed value and fire change so variant pricing updates.
          if (firstAllowed && (sel.value === '' || allowed[sel.value] !== true)) {
            sel.value = firstAllowed;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      // If the hidden selected-variant input points to an ineligible variant,
      // rewrite it to the first eligible variant so add-to-cart submits a
      // subscription-capable variant.
      var hidden = card.querySelector('.product-card-enhanced__selected-variant');
      if (hidden) {
        var currentId = parseInt(hidden.value, 10);
        var current = variants.find(function (v) { return v.id === currentId; });
        if (!current || !current.has_selling_plans) {
          hidden.value = eligible[0].id;
          hidden.dataset.selectedVariantId = eligible[0].id;
          var formInput = card.querySelector('input[name="id"]');
          if (formInput) formInput.value = eligible[0].id;
        }
      }
    });
  }

  function initCarousel(root) {
    var carousel = root.querySelector('[data-sns-carousel]');
    if (!carousel) return;

    var viewport = carousel.querySelector('[data-sns-viewport]');
    var track = carousel.querySelector('[data-sns-track]');
    var prev = carousel.querySelector('[data-sns-prev]');
    var next = carousel.querySelector('[data-sns-next]');
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-sns-slide]'));
    var tabButtons = Array.prototype.slice.call(root.querySelectorAll('[data-sns-tab]'));

    if (!slides.length) return;

    var currentIndex = 0;
    // First tab is "All" by convention — its handle (whatever it is) means "show everything".
    var allHandle = tabButtons.length ? (tabButtons[0].dataset.snsTab || '').toLowerCase() : '';

    function getCardGapPx() {
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0');
      return isNaN(gap) ? 20 : gap;
    }

    function getVisibleSlides() {
      return slides.filter(function (s) { return s.dataset.snsHidden !== 'true'; });
    }

    function getStep() {
      var visible = getVisibleSlides();
      if (!visible.length) return 1;
      var slideWidth = visible[0].getBoundingClientRect().width;
      var viewportWidth = viewport.getBoundingClientRect().width;
      var perPage = Math.max(1, Math.round(viewportWidth / (slideWidth + getCardGapPx())));
      return Math.min(perPage, visible.length);
    }

    function maxIndex() {
      var visible = getVisibleSlides();
      var step = getStep();
      return Math.max(0, visible.length - step);
    }

    function update() {
      var visible = getVisibleSlides();
      if (!visible.length) {
        track.style.transform = 'translateX(0)';
        if (prev) prev.disabled = true;
        if (next) next.disabled = true;
        return;
      }

      currentIndex = Math.max(0, Math.min(currentIndex, maxIndex()));
      var target = visible[currentIndex];
      if (!target) {
        track.style.transform = 'translateX(0)';
        return;
      }

      var trackRect = track.getBoundingClientRect();
      var targetRect = target.getBoundingClientRect();
      var offset = targetRect.left - trackRect.left;

      track.style.transform = 'translateX(' + (-offset) + 'px)';

      if (prev) prev.disabled = currentIndex <= 0;
      if (next) next.disabled = currentIndex >= maxIndex();
    }

    function go(delta) {
      var step = getStep();
      currentIndex = Math.max(0, Math.min(currentIndex + (delta * step), maxIndex()));
      update();
    }

    function applyFilter(handle) {
      var active = (handle || '').toLowerCase();
      slides.forEach(function (slide) {
        if (!active || active === allHandle) {
          slide.dataset.snsHidden = 'false';
          return;
        }
        var collections = (slide.dataset.productCollections || '').split(/\s+/).filter(Boolean);
        slide.dataset.snsHidden = collections.indexOf(active) !== -1 ? 'false' : 'true';
      });
      currentIndex = 0;
      // Reset transform immediately, then update positions after layout.
      track.style.transform = 'translateX(0)';
      requestAnimationFrame(update);
    }

    tabButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        tabButtons.forEach(function (b) {
          b.classList.remove('sns-tabs__link--active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('sns-tabs__link--active');
        btn.setAttribute('aria-selected', 'true');
        applyFilter(btn.dataset.snsTab);
      });
    });

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    var touchStartX = null;
    viewport.addEventListener('touchstart', function (e) {
      if (e.touches && e.touches.length === 1) touchStartX = e.touches[0].clientX;
    }, { passive: true });

    viewport.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var endX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : touchStartX;
      var diff = touchStartX - endX;
      if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1);
      touchStartX = null;
    });

    viewport.setAttribute('tabindex', '0');
    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(update, 120);
    });

    requestAnimationFrame(update);
    window.addEventListener('load', update);
  }

  /* Smooth scroll the static hero banner click to its in-page target. */
  function initHeroScroll(root) {
    var banner = root.querySelector('.sns-hero');
    if (!banner) return;
    var href = banner.getAttribute('href') || '';
    if (href.charAt(0) !== '#') return;
    banner.addEventListener('click', function (e) {
      var target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function init(root) {
    filterIneligibleVariants(root);
    initSubscribeRadios(root);
    preloadCarouselImages(root);
    initCarousel(root);
    initHeroScroll(root);
  }

  function boot() {
    document.querySelectorAll('.shopify-section--subscribe-save').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('shopify-section--subscribe-save')) {
      init(e.target);
    }
  });
})();
